import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ChevronLeft } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

/**
 * Public-facing privacy policy. Accessible at /privacy without auth so
 * it can be submitted as the privacy URL to Apple App Store Connect
 * and Google Play Console, and linked from the in-app profile menu.
 *
 * Maintained by hand — when materially changing data flows, update the
 * "Last updated" date below and add a one-line summary at the top.
 */
const LAST_UPDATED = "February 8, 2026";

const SECTIONS = [
    { id: "summary",       label: "1. The 30-second summary" },
    { id: "data",          label: "2. Information we collect" },
    { id: "use",           label: "3. How we use your information" },
    { id: "ai",            label: "4. AI processing of your plans" },
    { id: "sharing",       label: "5. Who we share data with" },
    { id: "subprocessors", label: "6. Third-party sub-processors" },
    { id: "security",      label: "7. How we protect your data" },
    { id: "retention",     label: "8. How long we keep your data" },
    { id: "rights",        label: "9. Your rights & choices" },
    { id: "children",      label: "10. Children's privacy" },
    { id: "international", label: "11. International transfers" },
    { id: "changes",       label: "12. Changes to this policy" },
    { id: "contact",       label: "13. How to reach us" },
];

export default function PrivacyPolicyPage() {
    const navigate = useNavigate();

    // Smooth-scroll TOC links inside this page only (defensive in case
    // CSS scroll-behavior is overridden by a parent container).
    useEffect(() => {
        document.title = "Privacy Policy — Steeltooth";
    }, []);

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0A1128] text-slate-200 flex flex-col"
            data-testid="privacy-page"
        >
            {/* Slim top bar — no full TopNav so the page works for
                logged-out visitors and app-store reviewers alike. */}
            <header className="sticky top-0 z-30 border-b border-[#2A3655] bg-[#0A1128]/95 backdrop-blur-xl">
                <div className="max-w-4xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
                        data-testid="privacy-back-button"
                        className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider font-bold text-slate-400 hover:text-white transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    <Link to="/" className="flex items-center" data-testid="privacy-brand">
                        <BrandLogo />
                    </Link>
                </div>
            </header>

            <main className="flex-1 px-4 sm:px-8 py-8 sm:py-12 pb-44 max-w-4xl mx-auto w-full">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#00A3FF] font-bold">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Privacy Policy
                </div>
                <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-white">
                    How we handle your data
                </h1>
                <p className="mt-3 text-sm text-slate-400">
                    Last updated: <span className="text-slate-200 font-semibold">{LAST_UPDATED}</span>
                </p>

                {/* Table of contents */}
                <nav
                    className="mt-8 rounded-md border border-[#2A3655] bg-[#0F1B3A] p-4"
                    aria-label="Table of contents"
                    data-testid="privacy-toc"
                >
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                        Contents
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                        {SECTIONS.map((s) => (
                            <li key={s.id}>
                                <a
                                    href={`#${s.id}`}
                                    className="text-[13px] text-slate-300 hover:text-[#00A3FF]"
                                >
                                    {s.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <article className="prose-policy mt-10 space-y-10 text-[15px] leading-relaxed">

                    <Section id="summary" title="1. The 30-second summary">
                        <p>
                            Steeltooth is a construction estimating and takeoff
                            tool for contractors. We collect the minimum data
                            needed to run the app: your account info, the
                            project files you upload, the estimates and quotes
                            you generate, and basic usage logs. We do not sell
                            your data, do not run third-party advertising
                            networks, and do not use your data to train
                            general-purpose AI models. Plan files and account
                            PII are encrypted at rest with AES-256-GCM. You can
                            export or delete everything we hold about you at
                            any time from <strong>Settings → Account</strong>.
                        </p>
                    </Section>

                    <Section id="data" title="2. Information we collect">
                        <SubHeading>2.1 Account information you give us</SubHeading>
                        <ul>
                            <li><strong>Email address</strong> (or Google account, if you sign in with Google).</li>
                            <li><strong>Password</strong> — stored only as a one-way bcrypt hash; the plaintext never touches our database.</li>
                            <li><strong>Full name, company name, phone number, birthdate</strong> — provided during sign-up.</li>
                            <li><strong>Profile picture</strong> (optional) — uploaded by you or pulled from your Google profile.</li>
                        </ul>

                        <SubHeading>2.2 Content you create inside the app</SubHeading>
                        <ul>
                            <li><strong>Project files</strong> — PDF plans, blueprint images, scanned documents, and any other files you upload.</li>
                            <li><strong>Takeoffs &amp; estimates</strong> — the line items, quantities, prices, and AI-generated summaries you produce.</li>
                            <li><strong>Quotes</strong> — customer-facing quote documents, including any logos or contact info you add to them.</li>
                            <li><strong>Templates &amp; pricing</strong> — the rate cards and unit prices you save for reuse.</li>
                            <li><strong>Leads &amp; contacts</strong> — names, emails, phone numbers and notes for the prospects and clients you track.</li>
                            <li><strong>Workgroup chats &amp; shared files</strong> — messages, attachments and activity logged inside a workgroup.</li>
                        </ul>

                        <SubHeading>2.3 Information collected automatically</SubHeading>
                        <ul>
                            <li><strong>Authentication tokens</strong> — a JSON Web Token stored in your browser's <code>localStorage</code> (key: <code>planflow.auth_token</code>), valid for 30 days.</li>
                            <li><strong>IP address</strong> — recorded on each login and used for security (ban evasion, abuse detection).</li>
                            <li><strong>Device / browser metadata</strong> — user-agent string, screen size at the time of a crash report.</li>
                            <li><strong>AI-usage ledger</strong> — per-call cost in tokens and credits for every Anthropic / OpenAI / Gemini request you trigger. Used for billing and rate-limiting.</li>
                            <li><strong>Crash reports</strong> — when the app's frontend crashes, the error message, stack trace, URL and your user-id are POSTed to our <code>/api/client-errors</code> endpoint so we can fix the bug.</li>
                        </ul>

                        <SubHeading>2.4 Information we do <em>not</em> collect</SubHeading>
                        <ul>
                            <li>No precise geolocation.</li>
                            <li>No advertising identifiers (IDFA / GAID).</li>
                            <li>No tracking pixels or third-party advertising SDKs.</li>
                            <li>No microphone / camera access without your explicit consent (used only when you tap "Add photo" inside a project).</li>
                        </ul>
                    </Section>

                    <Section id="use" title="3. How we use your information">
                        <p>We use the data above only to:</p>
                        <ul>
                            <li>Provide and maintain the app (sign you in, save your projects, render your quotes).</li>
                            <li>Generate AI estimates and takeoffs that you explicitly request.</li>
                            <li>Process subscription payments through our payment partner (see §6).</li>
                            <li>Send transactional emails (welcome, invoices, password resets, workgroup invites). We do <strong>not</strong> send marketing emails.</li>
                            <li>Protect against fraud, abuse and unauthorised access.</li>
                            <li>Comply with legal obligations (subpoenas, tax records, App Store / Play Store requirements).</li>
                        </ul>
                        <p>
                            We do <strong>not</strong> use your project data, estimates, or contact lists
                            to train any AI model — yours or anyone else's.
                        </p>
                    </Section>

                    <Section id="ai" title="4. AI processing of your plans">
                        <p>
                            When you tap <strong>Analyze</strong>, <strong>Run takeoff</strong> or
                            <strong> Generate estimate</strong>, the relevant pages of your PDF and
                            any notes you've written are sent to a large-language-model
                            provider (Anthropic Claude by default; optionally OpenAI GPT, Google
                            Gemini, or xAI Grok if your superadmin has selected a different
                            provider).
                        </p>
                        <ul>
                            <li>Requests use the provider's <strong>standard API</strong> (not the consumer chat product) and travel over HTTPS.</li>
                            <li>By default, all four providers operate under <strong>"zero-data-retention"</strong> commercial terms — i.e., they do not retain your prompts or completions beyond the time needed to return a response, and they do not use them for model training.</li>
                            <li>We log only the <em>metadata</em> of each call (model name, input token count, output token count, cost, timestamp) so we can meter your subscription credits. The plan text itself is not persisted to our LLM-usage ledger.</li>
                            <li>You can avoid AI processing entirely by skipping the Analyze / Takeoff / Estimate features — everything else in Steeltooth works without ever calling an AI provider.</li>
                        </ul>
                    </Section>

                    <Section id="sharing" title="5. Who we share data with">
                        <p>We do not sell or rent your personal data. We share it only with:</p>
                        <ul>
                            <li><strong>Sub-processors</strong> listed in §6 below, strictly so they can perform a service for us (hosting, payments, AI, email delivery).</li>
                            <li><strong>People you invite</strong> — when you add a teammate to a workgroup, that teammate can see the projects, files, and chats you share with that workgroup.</li>
                            <li><strong>Recipients of your quotes</strong> — when you send a public quote link, the customer can view that one quote (and any logo you attached) without an account.</li>
                            <li><strong>Law enforcement</strong>, when compelled by a valid subpoena, court order, or other legal process.</li>
                            <li><strong>A buyer or successor</strong>, in the unlikely event of a merger, acquisition or sale of substantially all our assets. You will be notified by email of any such change.</li>
                        </ul>
                    </Section>

                    <Section id="subprocessors" title="6. Third-party sub-processors">
                        <p>
                            The services below process some portion of your data on our
                            behalf. Each of them has signed a Data Processing Agreement (or
                            published one publicly) committing to GDPR-level protections.
                        </p>
                        <div className="mt-3 overflow-x-auto rounded-md border border-[#2A3655]">
                            <table className="w-full text-sm">
                                <thead className="bg-[#1C253C] text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Provider</th>
                                        <th className="px-3 py-2 text-left">Purpose</th>
                                        <th className="px-3 py-2 text-left">Data handled</th>
                                    </tr>
                                </thead>
                                <tbody className="text-slate-300 [&>tr]:border-t [&>tr]:border-[#2A3655]">
                                    <tr><td className="px-3 py-2">Anthropic (Claude)</td><td className="px-3 py-2">AI estimates, takeoffs, analyses</td><td className="px-3 py-2">Plan text + notes you submit to the AI</td></tr>
                                    <tr><td className="px-3 py-2">OpenAI</td><td className="px-3 py-2">Optional alternative AI provider</td><td className="px-3 py-2">Same as above (only if selected by you / your admin)</td></tr>
                                    <tr><td className="px-3 py-2">Google (Gemini)</td><td className="px-3 py-2">Optional alternative AI provider</td><td className="px-3 py-2">Same as above</td></tr>
                                    <tr><td className="px-3 py-2">xAI (Grok)</td><td className="px-3 py-2">Optional alternative AI provider</td><td className="px-3 py-2">Same as above</td></tr>
                                    <tr><td className="px-3 py-2">Google Identity Services</td><td className="px-3 py-2">"Sign in with Google" authentication</td><td className="px-3 py-2">Verified email, name and profile picture</td></tr>
                                    <tr><td className="px-3 py-2">RevenueCat → Stripe</td><td className="px-3 py-2">Subscription billing &amp; receipts</td><td className="px-3 py-2">User ID, plan, payment status (we never see your card number)</td></tr>
                                    <tr><td className="px-3 py-2">Resend</td><td className="px-3 py-2">Transactional email delivery</td><td className="px-3 py-2">Your email address + the message body of each email we send you</td></tr>
                                    <tr><td className="px-3 py-2">Emergent Object Storage</td><td className="px-3 py-2">File hosting (plan PDFs, logos, attachments)</td><td className="px-3 py-2">Uploaded files; access-scoped to your user account</td></tr>
                                    <tr><td className="px-3 py-2">Self-hosted MongoDB</td><td className="px-3 py-2">Primary database</td><td className="px-3 py-2">All account &amp; project records, encrypted at rest</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    <Section id="security" title="7. How we protect your data">
                        <ul>
                            <li><strong>Encryption in transit</strong> — every connection between your device and Steeltooth uses TLS 1.2+ (HTTPS).</li>
                            <li><strong>Encryption at rest</strong> — all PII fields on user records (email, full name, phone, company, birthdate, profile picture) are encrypted with <strong>AES-256-GCM</strong>. Lookups happen through an HMAC-SHA256 "blind index" so the plaintext email never touches the database query log.</li>
                            <li><strong>Password storage</strong> — bcrypt with a per-password salt. We do not have a way to recover your password; resets generate a new credential.</li>
                            <li><strong>Authentication tokens</strong> — short-lived JWT (30-day expiry) signed with HS256.</li>
                            <li><strong>Privilege separation</strong> — three roles exist (<code>user</code>, <code>admin</code>, <code>superadmin</code>) plus a sandboxed <code>reviewer</code> role used by App Store / Play Store reviewers. Integration secrets and AI prompts can only be modified by the platform owner.</li>
                            <li><strong>Audit trail</strong> — login IPs, role changes, ban actions and reviewer accesses are logged so we can investigate suspicious activity.</li>
                            <li><strong>Daily off-host backups</strong> of the database, retained for 30 days.</li>
                        </ul>
                        <p>
                            No system is ever 100% secure. If we ever discover a breach
                            affecting your data, we'll notify you by email within 72
                            hours of confirming the incident, per GDPR / state-law
                            breach-notification rules.
                        </p>
                    </Section>

                    <Section id="retention" title="8. How long we keep your data">
                        <ul>
                            <li><strong>Account record</strong> — kept while your account is active; deleted within 30 days of you deleting the account.</li>
                            <li><strong>Project files, estimates, quotes</strong> — same as the account record; deleted with it.</li>
                            <li><strong>Crash reports</strong> — kept for 90 days, then automatically purged.</li>
                            <li><strong>AI usage ledger</strong> — kept for 12 months for billing reconciliation, then aggregated and the per-call rows deleted.</li>
                            <li><strong>Invoice records</strong> — kept for 7 years to meet tax / accounting requirements (most jurisdictions require this).</li>
                            <li><strong>Banned-account hashes</strong> — kept indefinitely to prevent re-registration with a banned email or IP.</li>
                        </ul>
                    </Section>

                    <Section id="rights" title="9. Your rights &amp; choices">
                        <p>Wherever you live, you can:</p>
                        <ul>
                            <li><strong>Access</strong> — see every piece of data we hold about you by emailing <a href="mailto:privacy@planflow.pro" className="text-[#00A3FF] hover:underline">privacy@planflow.pro</a>. We respond within 30 days.</li>
                            <li><strong>Export</strong> — download all your projects, templates, leads and contacts as JSON/CSV from <strong>Settings → Account → Export my data</strong>.</li>
                            <li><strong>Correct</strong> — edit your name, email, phone, picture and password directly inside the app.</li>
                            <li><strong>Delete</strong> — permanently delete your account and all associated content from <strong>Settings → Account → Delete account</strong>. This is irreversible.</li>
                            <li><strong>Object</strong> — opt out of any specific processing by emailing <a href="mailto:privacy@planflow.pro" className="text-[#00A3FF] hover:underline">privacy@planflow.pro</a>. If we can't honour the request (e.g. legally required record-keeping) we'll tell you why.</li>
                            <li><strong>Complain</strong> — lodge a complaint with your local data-protection authority (GDPR Art. 77, CCPA §1798.155, etc.).</li>
                        </ul>
                        <p>
                            <strong>For California residents (CCPA / CPRA):</strong> we do not sell or
                            share your personal information for cross-context behavioural
                            advertising; therefore no "Do Not Sell or Share My Personal
                            Information" link is required, but you may still request access,
                            deletion and correction as above.
                        </p>
                        <p>
                            <strong>For EU / UK residents (GDPR / UK-GDPR):</strong> the legal basis for
                            most of our processing is the <em>performance of the contract</em>
                            between you and Steeltooth (Art. 6(1)(b)). Marketing email is
                            not sent; analytics is limited to first-party billing telemetry
                            (Art. 6(1)(f) legitimate interest).
                        </p>
                    </Section>

                    <Section id="children" title="10. Children's privacy">
                        <p>
                            Steeltooth is a B2B tool intended for licensed contractors and
                            their teams. It is not directed at children under 16, and we do
                            not knowingly collect personal data from anyone under 16. If you
                            believe a child has provided us with personal data, contact us at
                            <a href="mailto:privacy@planflow.pro" className="text-[#00A3FF] hover:underline"> privacy@planflow.pro</a> and we'll delete it.
                        </p>
                    </Section>

                    <Section id="international" title="11. International data transfers">
                        <p>
                            Steeltooth is operated from servers physically located in
                            North America. If you access the service from outside North
                            America, your data will be transferred to and processed in the
                            United States and Canada, where data-protection laws may differ
                            from those of your home country. For EU / UK users, we rely on
                            <strong> Standard Contractual Clauses</strong> with our sub-processors as the
                            lawful transfer mechanism.
                        </p>
                    </Section>

                    <Section id="changes" title="12. Changes to this policy">
                        <p>
                            We'll update the "Last updated" date at the top of this page
                            whenever we change this policy. If the change materially
                            affects how we collect or use your data, we'll also notify you
                            by email (and via an in-app banner) at least 14 days before the
                            change takes effect, giving you time to delete your account if
                            you disagree.
                        </p>
                    </Section>

                    <Section id="contact" title="13. How to reach us">
                        <p>
                            For any privacy question, request or complaint:
                        </p>
                        <ul>
                            <li>Email: <a href="mailto:privacy@planflow.pro" className="text-[#00A3FF] hover:underline">privacy@planflow.pro</a></li>
                            <li>General support: <a href="mailto:support@planflow.pro" className="text-[#00A3FF] hover:underline">support@planflow.pro</a></li>
                            <li>Mail: Steeltooth, c/o the account owner listed on your invoice.</li>
                        </ul>
                        <p className="text-[12px] text-slate-500 mt-6">
                            This policy is written in plain English for clarity and does not
                            replace legal advice. By using Steeltooth you confirm you
                            understand and agree to this policy and to the
                            {" "}
                            <Link to="/terms" className="text-[#00A3FF] hover:underline">Terms of Service</Link>.
                        </p>
                    </Section>

                </article>
            </main>
        </div>
    );
}

function Section({ id, title, children }) {
    return (
        <section
            id={id}
            data-testid={`privacy-section-${id}`}
            className="scroll-mt-20"
        >
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                {title}
            </h2>
            <div className="text-slate-300 space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:my-1 [&_p]:my-2 [&_code]:bg-[#0F1B3A] [&_code]:border [&_code]:border-[#2A3655] [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_code]:font-mono">
                {children}
            </div>
        </section>
    );
}

function SubHeading({ children }) {
    return (
        <h3 className="mt-5 mb-1 text-[13px] uppercase tracking-wider font-bold text-[#00A3FF]">
            {children}
        </h3>
    );
}
